import { useState, useEffect, useCallback } from 'react'

export const usePrescriptionTemplates = ({ apiClasses, apiType = 'physician' }) => {
    const [templates, setTemplates] = useState([])
    const [dispenseUnits, setDispenseUnits] = useState([])
    const [loading, setLoading] = useState(false)
    
    const searchTemplates = useCallback(async ({ 
        categoryId, 
        drugName, 
        strength, 
        DAW,
        stateId,
        ndc, 
        _t
    }) => {
        if (apiType === 'admin') {
            return []
        }
        
        const hasBothDrugNameAndStrength = !!drugName && !!strength;
        
        if (!categoryId && !hasBothDrugNameAndStrength) {
            return []
        }
        
        setLoading(true)
        try {
            
            
            if (!apiClasses.PhysicianAPI?.searchPrescriptionTemplates) {
                return []
            }

            const payload = {
                state: stateId,
                categoryId,
                drugName,
                strength,
                DAW,
                ndc,
            }

            if (_t) {
                payload._t = _t
            }
            
            const response = await apiClasses.PhysicianAPI.searchPrescriptionTemplates(payload)
            
            const templates = response.data?.templates || []
            setTemplates(templates || [])
            return templates || []
        } catch (error) {
            return []
        } finally {
            setLoading(false)
        }
    }, [apiClasses, apiType])
    
    const applyTemplate = useCallback(async ({
        template,
        templateAlternatives,
        requestedMedication,
        pharmacyId
    }) => {
        if (apiType === 'admin') {
            return null
        }
        
        if (!apiClasses?.PrescriptionsAPI?.getPharmacybyId) {
            return null
        }

        if (!dispenseUnits?.length) {
            return null
        }
        
        const { medication, pharmacyNotes, DAW } = template
        const { refills, daysSupply, pillCount, dispenseUnitId, drugName } = medication
        
        const dispenseUnit = dispenseUnits.find(unit => 
            unit.StandardDispenseUnitTypeID?.toString() === dispenseUnitId?.toString()
        )
        
        const prescriptionToQueue = {
            ...template,
            ingredients: null,
            ...medication,
            name: drugName,
            quantityUnits: dispenseUnit?.Name,
            providePharmacyNotes: !!pharmacyNotes,
            refills: Number(refills?.toString() || requestedMedication?.refills?.toString() || 0),
            daysSupply: Number(daysSupply?.toString() || requestedMedication?.daysSupply?.toString() || 30),
            quantity: pillCount?.toString() || requestedMedication?.pillCount?.toString() || 0,
            dispense: template.dispenseType || requestedMedication.dispense,
            doseForm: template.dispenseType || requestedMedication.dispense || dispenseUnit?.Name,
            startDate: new Date(),
            valid: false,
            noSubstitutions: !!DAW,
            _id: requestedMedication._id,
            requestedMedication,
            pharmacyId: template.pharmacyId || pharmacyId,
            templateAlternatives
        }
        
        if (prescriptionToQueue.pharmacyId) {
            try {
                const { data: { pharmacy } } = await apiClasses.PrescriptionsAPI.getPharmacybyId(prescriptionToQueue.pharmacyId)
                prescriptionToQueue.pharmacy = pharmacy
            } catch (error) {
                console.error(`Error fetching pharmacy details for ${drugName}:`, error)
            }
        }
        
        return prescriptionToQueue
    }, [apiClasses, dispenseUnits, apiType])
    
    const templatizePrescription = useCallback(async ({ 
        requestedMedication, 
        categoryId, 
        stateId, 
        pharmacyId 
    }) => {
        if (apiType === 'admin') {
            return null
        }

        const templates = await searchTemplates({
            categoryId,
            drugName: requestedMedication.drugName,
            strength: requestedMedication.dosage,
            DAW: requestedMedication.DAW ?? false,
            ndc: requestedMedication.ndc,
            stateId, 
        })
        
        if (!templates?.[0]) {
            return null
        }
        
        return applyTemplate({
            template: templates[0],
            templateAlternatives: templates,
            requestedMedication,
            pharmacyId
        })
    }, [dispenseUnits, searchTemplates, applyTemplate, apiType])
    
    const fetchDispenseUnits = useCallback(async () => {
        if (!apiClasses?.PrescriptionsAPI?.getAllAvailableDispenseUnits) {
            return
        }
                
        try {
            const { data } = await apiClasses.PrescriptionsAPI.getAllAvailableDispenseUnits()
            setDispenseUnits(data?.units || [])
        } catch (error) {
            console.error("Error fetching dispense units:", error)
        }
    }, [apiClasses, apiType])
    
    const getDispenseUnitById = useCallback((unitId) => {
        return dispenseUnits.find(unit => 
            unit.StandardDispenseUnitTypeID?.toString() === unitId?.toString()
        )
    }, [dispenseUnits])
    
    useEffect(() => {
        if (apiClasses?.PrescriptionsAPI) {
            fetchDispenseUnits()
        }
    }, [apiClasses?.PrescriptionsAPI])
    
    return {
        templates,
        dispenseUnits,
        loading,
        searchTemplates,
        applyTemplate,
        templatizePrescription,
        fetchDispenseUnits,
        getDispenseUnitById
    }
}
